import * as THREE from 'three';
import { cornerCam, flatCam, orbitCam } from '../scene/context.js';
import { syncNode } from '../scene/objects.js';
import { getTexture } from '../scene/textures.js';
import { requestRender } from '../state/bus.js';
import { pushHistory } from '../state/history.js';
import { $, S, clamp } from '../state/store.js';
import { drag, ray, setRay } from '../ui/interaction.js';
import { renderRails, setView } from '../ui/rails.js';
import { toast } from '../ui/toast.js';
import { isSiteLike } from '../scene/cameras.js';

/* ── mask editor: draws directly on whichever view is active ──────────────
   Click to drop polygon points on the selected image; straight edges
   connect them. Points are draggable and stored in normalised image space
   so they hold across aspect changes. Committing cuts the image plane to
   the polygon (or punches it out, if inverted) so parts of the image read
   as standing proud of the recess.

   One implementation, not one per view. On-site warps the polygon onto a
   real photo via a homography (the same corner solve blitSite uses) — the
   one surface actually worth switching to, since it shows exactly where
   the cut lands on the building. Every other view has no photo to align
   against, so a point is placed with a plain camera projection instead —
   same idea, no warp needed. Split shows two surfaces (Orbit + Flat) at
   once, so it's the one case where more than one pane paints simultaneously
   and either can independently receive clicks. */
export const mask = { obj:null, pts:[], invert:false, active:false, drag:-1, sel:-1, dragSurface:null };

/* clicking the object list's Remove works regardless of what's open in the
   editor. Committing or closing used to call syncNode(obj) unconditionally,
   which rebuilds a THREE node and adds it back to the scene even though the
   object no longer exists in S.objects — a rendering with no corresponding
   row anywhere, impossible to select or delete because nothing refers to it
   any more except this editor's own dangling reference. */
function isLive(obj){ return !!obj && S.objects.includes(obj); }

function imageMeshOf(obj){
  let mesh = null; obj.node && obj.node.traverse(c=>{ if(c.isMesh) mesh = c; }); return mesh;
}
function imagePlaneHW(obj){                       // logical rect (w,h) of the image plane
  const w = obj.params.w;
  const e = getTexture(obj.params.src);
  return { w, h: w/((e && e.aspect) || 1) };
}

/* Every surface currently showing the scene, each able to host mask editing
   independently — one everywhere except Split, which shows two at once.
   #fitbox is the one live orbit canvas, reused for standalone Orbit/Flat
   and Split's left pane (see ui/chrome.js's setView); its own camera
   follows S.view the same way activeCam()/isOrbitLike() already do. */
function activeSurfaces(){
  if(isSiteLike()){
    const cv = $('#siteCv');
    if(!cv) return [];
    return [{ svg:$('#siteMaskSvg'), canvasEl:cv, cam:flatCam, warp:true, el:$('#siteWrap') }];
  }
  if(S.view==='split'){
    return [
      { svg:$('#fitboxMaskSvg'), canvasEl:$('#cv'), cam:orbitCam, warp:false, el:$('#fitbox') },
      { svg:$('#splitMaskSvg'), canvasEl:$('#cvSplitR'), cam:flatCam, warp:false, el:$('#fitboxSplitR') },
    ];
  }
  if(S.view==='orbit' || S.view==='flat'){
    const first = { svg:$('#fitboxMaskSvg'), canvasEl:$('#cv'),
                    cam: S.view==='orbit' ? orbitCam : flatCam, warp:false, el:$('#fitbox') };
    /* A corner screen's second panel is its own pane with its own camera, so
       it is its own surface — exactly the shape Split already needs. Flat only:
       there the two panes are separate elements, so the pointer event's own
       target says which panel it belongs to and nothing has to be worked out.
       On-site puts both panels inside one wrapper and would have to hit-test
       the marked quads instead; until that is done, masking a corner is a Flat
       job and the bar says so. */
    if(S.view==='flat' && S.cornerPanel && !$('#fitboxCornerR').classList.contains('hidden'))
      return [first, { svg:$('#cornerMaskSvg'), canvasEl:$('#cvCornerR'),
                       cam:cornerCam, warp:false, el:$('#fitboxCornerR') }];
    return [first];
  }
  return [];   // Calibrate — nothing to mask against
}
/* Which surface a specific pointer event belongs to. Correct for the click
   that just fired (ev.currentTarget is exactly that surface's element,
   since interaction.js's listeners are bound per-pane) and, passed
   explicitly, for a mask-point drag continuing outside it — see the
   window-level pointermove listener below, which resolves this once at
   drag-start (mask.dragSurface) rather than trying to re-derive it from an
   event whose currentTarget is window by then. */
function surfaceForEvent(ev){
  return activeSurfaces().find(s => s.el === ev.currentTarget) || activeSurfaces()[0] || null;
}

// mask point [u,v_img] -> screen pixel on `surface`, via the object's world
// transform and whichever camera/warp that surface uses.
function maskPtToPx(surface, obj, pt){
  const {w,h} = imagePlaneHW(obj);
  const x = (pt[0]-0.5)*w, y = (0.5-pt[1])*h;
  const wp = new THREE.Vector3(x, y, 0).applyMatrix4(obj.node.matrixWorld);
  wp.project(surface.cam);
  const cv = surface.canvasEl;
  // On-site's SVG is sized to the RENDER's own drawing-buffer pixels and
  // shares its CSS warp (paintMask, below); everywhere else the SVG just
  // fills its pane, so CSS pixels are what its viewBox is set to.
  const W = surface.warp ? cv.width : cv.clientWidth;
  const H = surface.warp ? cv.height : cv.clientHeight;
  return [ (wp.x*0.5+0.5)*W, (1-(wp.y*0.5+0.5))*H ];
}
// Perpendicular distance from p to the segment a-b, clamped to the segment
// itself (not the infinite line) — a click past either endpoint measures to
// that endpoint, not to a point off the visible edge.
function distToSegment(p, a, b){
  const dx = b[0]-a[0], dy = b[1]-a[1];
  const lenSq = dx*dx + dy*dy;
  const t = lenSq > 1e-9 ? Math.max(0, Math.min(1, ((p[0]-a[0])*dx + (p[1]-a[1])*dy) / lenSq)) : 0;
  return Math.hypot(p[0] - (a[0]+t*dx), p[1] - (a[1]+t*dy));
}
// Where a new point at `hit` should be spliced in: right after whichever
// edge of the current polygon it's closest to. Fewer than 2 existing points
// means there's no edge yet to compare against — just append. The closing
// edge (last point back to first) only exists once there are 3+ points,
// same threshold paintMask uses to draw a <polygon> instead of a
// <polyline>. `surface` defaults to whichever the caller doesn't specify
// one for (fine whenever there's only one active, i.e. everywhere but
// Split) — the real click handler below always passes the one the event
// actually belongs to.
export function insertionIndexForClosestEdge(hit, surface = activeSurfaces()[0]){
  const n = mask.pts.length;
  if(n < 2 || !surface) return n;
  const hitPx = maskPtToPx(surface, mask.obj, hit);
  const ptsPx = mask.pts.map(p => maskPtToPx(surface, mask.obj, p));
  const edgeCount = n >= 3 ? n : n-1;
  let bestI = 0, bestD = Infinity;
  for(let i=0; i<edgeCount; i++){
    const d = distToSegment(hitPx, ptsPx[i], ptsPx[(i+1)%n]);
    if(d < bestD){ bestD = d; bestI = i; }
  }
  return bestI + 1;
}
// raycast a pointer event to the image plane -> [u, v_img], or null.
// Camera-agnostic via setRay's own resolution (ui/interaction.js) unless a
// specific surface is supplied, for the same reason insertionIndexForClosestEdge
// takes one — a captured drag whose event no longer carries it.
function maskHit(ev, surface){
  const mesh = imageMeshOf(mask.obj);
  if(!mesh) return null;
  const ok = surface ? setRay(ev, { cam:surface.cam, el:surface.el }) : setRay(ev);
  if(!ok) return null;
  const hits = ray.intersectObject(mesh, true);
  if(!hits.length || !hits[0].uv) return null;
  return [ hits[0].uv.x, 1 - hits[0].uv.y ];      // three uv is bottom-up; image v is top-down
}

function paintOneSurface(surface){
  const { svg, canvasEl } = surface;
  if(!svg || !canvasEl) return;
  if(surface.warp){
    /* Keeps the polygon glued to the render's own warp (and the on-site
       pan/zoom, folded into the same transform) rather than duplicating
       that math here. */
    svg.setAttribute('viewBox', `0 0 ${canvasEl.width} ${canvasEl.height}`);
    svg.style.width = canvasEl.width+'px'; svg.style.height = canvasEl.height+'px';
    svg.style.transform = canvasEl.style.transform;
  }else{
    svg.setAttribute('viewBox', `0 0 ${canvasEl.clientWidth} ${canvasEl.clientHeight}`);
    svg.style.width = '100%'; svg.style.height = '100%';
    svg.style.transform = 'none';
  }
  svg.classList.remove('hidden');
  const px = mask.pts.map(p => maskPtToPx(surface, mask.obj, p));
  let s = '';
  if(px.length>=3) s += '<polygon class="medge" points="'+px.map(p=>p[0].toFixed(1)+','+p[1].toFixed(1)).join(' ')+'"/>';
  else if(px.length===2) s += '<polyline class="medge" style="fill:none" points="'+px.map(p=>p[0].toFixed(1)+','+p[1].toFixed(1)).join(' ')+'"/>';
  const refH = surface.warp ? canvasEl.height : canvasEl.clientHeight;
  const r = Math.max(5, refH*0.012);
  const rDot = r, rRing = r*1.9;
  px.forEach((p,i)=>{
    const cx=p[0].toFixed(1), cy=p[1].toFixed(1);
    const isSel = (i===mask.sel);
    if(isSel) s += '<circle class="mptring" cx="'+cx+'" cy="'+cy+'" r="'+rRing.toFixed(1)+'"/>';
    s += '<circle class="mpt" data-i="'+i+'" cx="'+cx+'" cy="'+cy+'" r="'+rDot.toFixed(1)+'"/>';
    s += '<circle class="mptdot'+(i===0?' first':'')+(isSel?' sel':'')+
         '" cx="'+cx+'" cy="'+cy+'" r="'+(isSel?rDot*1.15:rDot).toFixed(1)+'"/>';
  });
  svg.innerHTML = s;
}
export function paintMask(){
  if(!mask.active) return;
  activeSurfaces().forEach(paintOneSurface);
  const del = $('#maskDelete'); if(del) del.disabled = mask.sel < 0;
  const hint = $('#maskHint');
  /* On a corner screen only Flat shows both panels as separate panes, which is
     what lets a point be placed on the second one. Said here rather than left
     to be discovered by clicking on the second panel On-site and having nothing
     happen — which is exactly how this was reported. */
  const cornerElsewhere = S.cornerPanel && S.view !== 'flat';
  hint.textContent = cornerElsewhere
    ? 'Two screens: switch to Flat output to mask across the corner — points can only be placed on the second panel there.'
    : mask.pts.length < 3
      ? 'Click on the image to add points — at least three.'
      : (mask.sel >= 0
          ? 'Point ' + (mask.sel+1) + ' selected — drag to move, or Delete to remove.'
          : 'Click a point to select it, drag to move, or press Done.');
  hint.classList.toggle('maskhint--warn', !!cornerElsewhere);
}

function activate(obj){
  mask.obj = obj; mask.active = true; mask.sel = -1; mask.drag = -1; mask.dragSurface = null;
  mask.pts = (obj.mask && obj.mask.points ? obj.mask.points.map(p=>p.slice()) : []);
  mask.invert = !!(obj.mask && obj.mask.invert);
  $('#maskInvert').checked = mask.invert;
  obj._maskEdit = true; syncNode(obj);            // show the full plane while drawing
  $('#maskBar').classList.remove('hidden');
  activeSurfaces().forEach(s => s.el.classList.add('masking'));
  requestRender(); requestAnimationFrame(paintMask);
}
/** Open masking on whichever view is currently active — Orbit, Flat, Split
 *  or On-site, each drawing directly over its own render. */
export function openMask(obj){
  if(!obj || obj.type!=='image' || !obj.params.src){ toast('Select an image first.', true); return; }
  if(S.stage==='calib') return;
  activate(obj);
}
/** Same, but forces On-site first — worth it there specifically, since it's
 *  the one surface that shows exactly where the cut lands on a real photo. */
export function openSiteMask(obj){
  if(!obj || obj.type!=='image' || !obj.params.src){ toast('Select an image first.', true); return; }
  /* Only when there is no mockup on screen at all — in Split there already
     is one, and yanking the view over to standalone would take the scene pane
     away to show what is already visible. */
  if(!isSiteLike()) setView('site');
  activate(obj);
}
function closeMask(){
  const obj = mask.obj;
  /* _maskEdit shows the full, un-cut plane while drawing (activate). On
     Cancel that flag needs to come off AND the node needs rebuilding to
     match — without syncNode here the geometry stays the full plane even
     though the data says otherwise, until something unrelated happens to
     rebuild it. Commit already gets this right (commitMask calls syncNode);
     Cancel silently didn't. isLive guards both: an object deleted while the
     editor was open must not get a fresh node built and added back — see
     isLive's comment above. */
  if(obj){
    delete obj._maskEdit;
    if(isLive(obj)){ syncNode(obj); requestRender(); }
  }
  mask.active=false; mask.obj=null; mask.pts=[]; mask.drag=-1; mask.sel=-1; mask.dragSurface=null;
  $('#maskBar').classList.add('hidden');
  ['#fitboxMaskSvg','#splitMaskSvg','#siteMaskSvg','#cornerMaskSvg']
    .forEach(sel=>{ const el = $(sel); if(el) el.classList.add('hidden'); });
  document.querySelectorAll('.masking').forEach(el => el.classList.remove('masking'));
}
function commitMask(){
  const obj = mask.obj; if(!obj) return;
  if(!isLive(obj)){ closeMask(); return; }   // deleted while the editor was open
  if(mask.pts.length>=3) obj.mask = { on:true, invert:mask.invert, points:mask.pts.map(p=>p.slice()) };
  else if(obj.mask) obj.mask.on = false;
  delete obj._maskEdit;
  syncNode(obj); renderRails(); requestRender(); pushHistory();
  closeMask();
}
// mask-mode clicks are handled here, before normal object selection/drag —
// see ui/interaction.js's onDown.
export function maskPointer(ev){
  if(!mask.active) return false;
  const surface = surfaceForEvent(ev);
  if(!surface) return false;
  const t = ev.target;
  if(t && t.classList && t.classList.contains('mpt')){
    const i=+t.dataset.i;
    // clicking the first point still closes the polygon
    if(i===0 && mask.pts.length>=3 && mask.sel===0){ commitMask(); return true; }
    mask.sel = i;                 // this point is now selected
    mask.drag = i;                // and armed for dragging on the same gesture
    mask.dragSurface = surface;   // captured so the drag can continue once ev.currentTarget is no longer this pane
    paintMask();
    try{ surface.el.setPointerCapture(ev.pointerId); }catch(_){}
    return true;
  }
  // Clicking empty space adds a new point on whichever edge of the polygon
  // the click actually landed nearest — the standard path-editing
  // convention (click a segment, get a node there), and one that doesn't
  // depend on remembering to select the right point first. Distances are
  // compared in the same pixel space the preview itself is drawn in, so
  // "closest" matches what's on screen.
  const hit = maskHit(ev);
  if(hit){
    const at = insertionIndexForClosestEdge(hit, surface);
    mask.pts.splice(at, 0, hit);
    mask.sel = at;                        // the new one is selected
    paintMask();
  } else {
    mask.sel = -1; paintMask();
  }
  return true;
}
addEventListener('pointermove', ev=>{
  if(mask.drag<0) return;
  const hit = maskHit(ev, mask.dragSurface);
  if(hit){ mask.pts[mask.drag]=hit; paintMask(); }
});
addEventListener('pointerup', ()=>{ if(mask.drag>=0) mask.drag=-1; });
// Clicking anywhere outside every active masking surface commits the mask,
// same as pressing Done — a click on one of those surfaces always adds or
// selects a point instead (maskPointer, reached via interaction.js's onDown),
// so this only ever fires for a genuinely outside click. The mask toolbar
// itself (#maskBar: Delete/Undo/Clear/Cancel/Done/Invert) is excluded so its
// own buttons keep doing what they already do, Cancel included. Capture
// phase, at window, so it runs before whatever the click actually landed on
// reacts to it, but doesn't stop that from also happening afterward.
addEventListener('pointerdown', ev=>{
  if(!mask.active) return;
  const bar = $('#maskBar');
  if(bar && bar.contains(ev.target)) return;
  if(activeSurfaces().some(s => s.el && s.el.contains(ev.target))) return;
  commitMask();
}, true);
addEventListener('keydown', ev=>{
  if(!mask.active) return;
  const t = ev.target && ev.target.tagName;
  if(t==='INPUT'||t==='SELECT'||t==='TEXTAREA') return;
  /* In-progress points are local UI state, not part of the app's own undo
     history — the global Ctrl+Z (interaction.js) would silently do nothing
     useful for them, or worse, undo some unrelated earlier change, while
     leaving the polygon on screen untouched. Mirror the Undo button. This
     listener is already capture-phase, so it beats interaction.js's
     bubble-phase one to the keydown regardless of import order. */
  if((ev.metaKey||ev.ctrlKey) && ev.key.toLowerCase()==='z'){
    ev.preventDefault(); ev.stopPropagation();
    mask.pts.pop();
    if(mask.sel >= mask.pts.length) mask.sel = mask.pts.length-1;
    paintMask();
    return;
  }
  if((ev.key==='Delete' || ev.key==='Backspace') && mask.sel >= 0){
    ev.preventDefault(); ev.stopPropagation();
    deleteMaskPoint();
  } else if(ev.key==='Escape' && mask.sel >= 0){
    mask.sel = -1; paintMask();
  }
}, true);
$('#maskInvert').addEventListener('change', ()=>{ mask.invert = $('#maskInvert').checked; });
function deleteMaskPoint(){
  if(mask.sel < 0 || mask.sel >= mask.pts.length) return;
  mask.pts.splice(mask.sel, 1);
  // move the selection to the previous point (or clear it if none left)
  mask.sel = mask.pts.length ? Math.max(0, mask.sel - 1) : -1;
  paintMask();
}
$('#maskDelete').onclick = deleteMaskPoint;
$('#maskUndo').onclick   = ()=>{ mask.pts.pop(); if(mask.sel >= mask.pts.length) mask.sel = mask.pts.length-1; paintMask(); };
$('#maskClear').onclick  = ()=>{ mask.pts=[]; mask.sel=-1; paintMask(); };
$('#maskCancel').onclick = ()=>closeMask();
$('#maskDone').onclick   = ()=>commitMask();
